import Phaser from 'phaser';
import { showBanner } from '../AdManager';

// ─────────────────────────────────────────────────────────────────────────────
// SplashScene — animated sky + ground + stacking animal tower
// Original splashscreen.png is still loaded as a fallback key 'splash'.
// ─────────────────────────────────────────────────────────────────────────────
export default class SplashScene extends Phaser.Scene {
  constructor() {
    super({ key: 'SplashScene' });
  }

  preload() {
    this.load.image('splash',    'assets/images/splashscreen.png'); // fallback kept
    this.load.image('bear',      'assets/images/bear.png');
    this.load.image('elephant',  'assets/images/elephant.png');
    this.load.image('giraffe',   'assets/images/giraffe.png');
    this.load.image('lion',      'assets/images/lion.png');
    this.load.image('tiger',     'assets/images/tiger.png');
    this.load.image('rhino',     'assets/images/rhino.png');
    this.load.image('cat',       'assets/images/cat.png');
    this.load.image('fox',       'assets/images/fox.png');
    this.load.image('eagle',     'assets/images/eagle.png');
    this.load.image('cow',       'assets/images/cow.png');
    this.load.image('panda',     'assets/images/panda.png');
    this.load.image('pig',       'assets/images/pig.png');
    this.load.image('puppy',     'assets/images/puppy.png');
    this.load.image('horse',     'assets/images/horse.png');
    this.load.image('tiger2',    'assets/images/tiger2.png');

    // Force the browser to load Chewy before Phaser renders any text with it.
    // We do this during preload so it's ready by the time create() runs.
    this._chewyReady = false;
    document.fonts.load('1em Chewy').then(() => {
      this._chewyReady = true;
    }).catch(() => {
      this._chewyReady = true; // fallback — don't block forever
    });
  }

  create() {
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    // ── Sky gradient ──────────────────────────────────────────────
    const sky = this.add.graphics();
    sky.fillGradientStyle(0x4fc3f7, 0x4fc3f7, 0xb3e5fc, 0xb3e5fc, 1);
    sky.fillRect(0, 0, W, H);

    // ── Sun ───────────────────────────────────────────────────────
    const sunX = W * 0.80, sunY = H * 0.13, sunR = 36 * sd;
    const sunGfx = this.add.graphics();
    for (let i = 4; i >= 1; i--) {
      sunGfx.fillStyle(0xffe066, 0.05 * i);
      sunGfx.fillCircle(sunX, sunY, sunR + i * 14 * sd);
    }
    sunGfx.fillStyle(0xffd700, 1);
    sunGfx.fillCircle(sunX, sunY, sunR);
    sunGfx.fillStyle(0xffec6e, 1);
    sunGfx.fillCircle(sunX - sunR * 0.22, sunY - sunR * 0.22, sunR * 0.42);

    // ── Clouds ────────────────────────────────────────────────────
    const cloudDefs = [
      { x: W * 0.16, y: H * 0.09, s: 1.0 },
      { x: W * 0.52, y: H * 0.06, s: 0.75 },
      { x: W * 0.82, y: H * 0.19, s: 0.85 },
      { x: W * 0.36, y: H * 0.21, s: 0.65 },
    ];
    const clouds = cloudDefs.map(c => this._makeCloud(c.x, c.y, c.s * sd));
    clouds.forEach((c, i) => {
      this.tweens.add({
        targets: c, x: `+=${(16 + i * 6) * sd}`,
        duration: 3200 + i * 600, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    });

    // ── Distant rolling hills ─────────────────────────────────────
    const hillGfx = this.add.graphics();
    hillGfx.fillStyle(0x81c784, 0.55);
    hillGfx.fillEllipse(W * 0.12, H * 0.73, W * 0.58, H * 0.28);
    hillGfx.fillEllipse(W * 0.78, H * 0.71, W * 0.62, H * 0.26);
    hillGfx.fillStyle(0x66bb6a, 0.45);
    hillGfx.fillEllipse(W * 0.46, H * 0.75, W * 0.52, H * 0.22);

    // ── Ground ────────────────────────────────────────────────────
    const groundY = H * 0.77;
    const gndGfx  = this.add.graphics();
    gndGfx.fillGradientStyle(0x43a047, 0x43a047, 0x2e7d32, 0x2e7d32, 1);
    gndGfx.fillRect(0, groundY, W, H - groundY);
    // Grass tufts
    gndGfx.fillStyle(0x66bb6a, 1);
    for (let x = 6 * sd; x < W; x += 20 * sd) {
      const h = (5 + Math.sin(x * 0.05) * 3) * sd;
      gndGfx.fillTriangle(x, groundY, x + 7 * sd, groundY, x + 3.5 * sd, groundY - h);
    }

    // ── Stacked tower of animals (animates in one by one) ─────────
    const stackKeys  = ['elephant', 'rhino', 'bear', 'lion'];
    const blockSize  = 54 * sd;
    const towerBaseY = groundY - 4 * sd;
    const towerX     = W / 2;

    stackKeys.forEach((key, i) => {
      const finalY = towerBaseY - i * blockSize - blockSize / 2;
      const startY = finalY - 40 * sd;
      const img = this.add.image(towerX, startY, key)
        .setDisplaySize(blockSize, blockSize)
        .setAlpha(0);
      this.tweens.add({
        targets: img, alpha: 1, y: finalY,
        duration: 380, delay: 400 + i * 200, ease: 'Back.easeOut',
      });
    });

    // Tiny animals walking in from sides
    const walkers = [
      { key: 'cat',   x: -40 * sd, y: groundY - 20 * sd, size: 34 * sd, dir: 1,  targetX: W * 0.18 },
      { key: 'fox',   x: W + 40 * sd, y: groundY - 18 * sd, size: 30 * sd, dir: -1, targetX: W * 0.82, flipX: true },
      { key: 'tiger', x: -50 * sd, y: groundY - 22 * sd, size: 36 * sd, dir: 1,  targetX: W * 0.28 },
    ];
    walkers.forEach((w, i) => {
      const img = this.add.image(w.x, w.y, w.key)
        .setDisplaySize(w.size, w.size)
        .setAlpha(0)
        .setFlipX(w.flipX || false);
      this.tweens.add({
        targets: img, x: w.targetX, alpha: 1,
        duration: 1200, delay: 600 + i * 250, ease: 'Power2.easeOut',
      });
      // Gentle bob while walking
      this.tweens.add({
        targets: img, y: w.y - 4 * sd,
        duration: 400, delay: 700 + i * 250, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    });

    // ── Eagle flying overhead ─────────────────────────────────────
    const eagleY = H * 0.30;
    const eagle  = this.add.image(-60 * sd, eagleY, 'eagle')
      .setDisplaySize(48 * sd, 48 * sd)
      .setAlpha(0);
    this.tweens.add({
      targets: eagle, x: W + 60 * sd, alpha: 1,
      duration: 2800, delay: 300, ease: 'Power1.easeInOut',
      onComplete: () => eagle.destroy(),
    });
    // Wing flap (scale bob)
    this.tweens.add({
      targets: eagle, scaleY: `*=0.85`,
      duration: 220, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });

    // ── Game title — created after Chewy font is confirmed ready ──
    const line1Y = H * 0.34;
    const line2Y = line1Y + 46 * sd;

    const spawnTitle = () => {
      const shadow1 = this.add.text(W / 2 + 3 * sd, line1Y + 4 * sd, 'Animal', {
        fontSize: `${44 * sd}px`, fontFamily: 'Chewy, Arial Rounded MT Bold, Arial, sans-serif',
        color: '#1a4a1a',
      }).setOrigin(0.5).setAlpha(0);
      const shadow2 = this.add.text(W / 2 + 3 * sd, line2Y + 4 * sd, 'Stack', {
        fontSize: `${44 * sd}px`, fontFamily: 'Chewy, Arial Rounded MT Bold, Arial, sans-serif',
        color: '#1a4a1a',
      }).setOrigin(0.5).setAlpha(0);

      const title1 = this.add.text(W / 2, line1Y, 'Animal', {
        fontSize: `${44 * sd}px`, fontFamily: 'Chewy, Arial Rounded MT Bold, Arial, sans-serif',
        color: '#ffffff', stroke: '#2e7d32', strokeThickness: 7 * dpr,
      }).setOrigin(0.5).setAlpha(0);
      const title2 = this.add.text(W / 2, line2Y, 'Stack', {
        fontSize: `${44 * sd}px`, fontFamily: 'Chewy, Arial Rounded MT Bold, Arial, sans-serif',
        color: '#ffffff', stroke: '#2e7d32', strokeThickness: 7 * dpr,
      }).setOrigin(0.5).setAlpha(0);

      const subtitle = this.add.text(W / 2, line2Y + 48 * sd, 'How high can you go? \u{1F992}', {
        fontSize: `${15 * sd}px`,
        fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
        color: '#e8f5e9', stroke: '#1b5e20', strokeThickness: 3 * dpr,
      }).setOrigin(0.5).setAlpha(0);

      this.tweens.add({ targets: [shadow1, shadow2, title1, title2], alpha: 1, duration: 700, ease: 'Power2' });
      this.tweens.add({ targets: subtitle, alpha: 1, duration: 700, delay: 300, ease: 'Power2' });
      this.tweens.add({
        targets: [title1, title2, shadow1, shadow2], y: `-=${5 * sd}`,
        duration: 1500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    };

    // Poll until font is ready (usually instant on 2nd+ load, <300ms on first)
    const waitForFont = () => {
      if (this._chewyReady) {
        spawnTitle();
      } else {
        this.time.delayedCall(50, waitForFont);
      }
    };
    waitForFont();

    // ── Transition ────────────────────────────────────────────────
    this.time.delayedCall(3000, () => {
      this.cameras.main.fadeOut(500, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => this.scene.start('HomeScene'));
    });

    this.cameras.main.fadeIn(500, 0, 0, 0);

    showBanner();
  }

  _makeCloud(x, y, sd) {
    const g = this.add.graphics();
    g.fillStyle(0xffffff, 0.90);
    g.fillEllipse(x,           y,          58 * sd, 28 * sd);
    g.fillEllipse(x - 20 * sd, y + 7 * sd, 38 * sd, 22 * sd);
    g.fillEllipse(x + 22 * sd, y + 5 * sd, 42 * sd, 24 * sd);
    g.fillStyle(0xf0faff, 0.55);
    g.fillEllipse(x - 5 * sd,  y - 7 * sd, 28 * sd, 14 * sd);
    return g;
  }
}
